import React, { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Settings, Trash2, ChevronDown } from 'lucide-react';
import { MappingEndpointsSection } from './MappingEndpointsSection';
import { cn } from '@/lib/utils';

interface Bot {
  id: string;
  name: string;
  signal_token: string;
  type: string;
}

interface BotMapping {
  id: string;
  parent_bot_id: string;
  child_bot_id: string;
  is_active: boolean;
  notes: string | null;
  instrument: string | null;
  tp_usd: number | null;
  sl_usd: number | null;
  quantity: number | null;
  created_at: string;
  updated_at: string;
  parent_bot: Bot | null;
  child_bot: Bot | null;
}

interface ChildBotItemProps {
  mapping: BotMapping;
  onToggleActive: (mapping: BotMapping) => void;
  onDelete: (mapping: BotMapping) => void;
  isUpdating: boolean;
  isDeleting: boolean;
}

export const ChildBotItem: React.FC<ChildBotItemProps> = ({
  mapping,
  onToggleActive,
  onDelete,
  isUpdating,
  isDeleting,
}) => {
  const [showEndpoints, setShowEndpoints] = useState(false);
  const childBot = mapping.child_bot;
  const parentBot = mapping.parent_bot;

  if (!childBot || !parentBot) {
    return (
      <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-medium text-destructive text-sm">Invalid Mapping</p>
            <p className="text-xs text-muted-foreground">Bot đã bị xóa</p>
          </div>
          <Button
            variant="destructive"
            size="sm"
            onClick={() => onDelete(mapping)}
            disabled={isDeleting}
          >
            <Trash2 className="h-3 w-3" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'border rounded-lg transition-all',
        mapping.is_active ? 'bg-card border-border' : 'bg-muted/30 border-muted'
      )}
    >
      <div className="flex items-center justify-between gap-3 p-3">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="flex-1 min-w-0 space-y-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium text-sm truncate">{childBot.name}</span>
              <Badge variant="secondary" className="text-xs">
                {childBot.type}
              </Badge>
              {mapping.is_active ? (
                <Badge variant="default" className="text-xs">Active</Badge>
              ) : (
                <Badge variant="outline" className="text-xs">Inactive</Badge>
              )}
            </div>
            {mapping.notes && (
              <p className="text-xs text-muted-foreground truncate">{mapping.notes}</p>
            )}
            
            {/* Trading Config Display */}
            {mapping.instrument && mapping.tp_usd && mapping.sl_usd && mapping.quantity ? (
              <div className="flex items-center gap-2 text-xs text-muted-foreground flex-wrap">
                <span className="font-mono bg-muted px-1.5 py-0.5 rounded">📊 {mapping.instrument}</span>
                <span className="bg-green-500/10 text-green-700 dark:text-green-400 px-1.5 py-0.5 rounded">
                  TP: ${mapping.tp_usd}
                </span>
                <span className="bg-red-500/10 text-red-700 dark:text-red-400 px-1.5 py-0.5 rounded">
                  SL: ${mapping.sl_usd}
                </span>
                <span className="bg-blue-500/10 text-blue-700 dark:text-blue-400 px-1.5 py-0.5 rounded">
                  Qty: {mapping.quantity}
                </span>
              </div>
            ) : (
              <div className="flex items-center gap-1.5 text-xs">
                <Badge variant="outline" className="text-amber-600 border-amber-300 bg-amber-50 dark:bg-amber-950/20">
                  ⚠️ No Config
                </Badge>
                <span className="text-muted-foreground">Will use published analysis config</span>
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <Switch
            checked={mapping.is_active}
            onCheckedChange={() => onToggleActive(mapping)}
            disabled={isUpdating}
          />
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowEndpoints(!showEndpoints)}
            className="h-8 w-8 p-0"
          >
            <Settings className={cn(
              "h-3.5 w-3.5 transition-transform",
              showEndpoints && "rotate-90"
            )} />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onDelete(mapping)}
            disabled={isDeleting}
            className="h-8 w-8 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {showEndpoints && (
        <div className="border-t p-3 bg-muted/20">
          <MappingEndpointsSection
            mappingId={mapping.id}
            parentBotId={mapping.parent_bot_id}
            parentBotName={parentBot.name}
            childBotName={childBot.name}
          />
        </div>
      )}
    </div>
  );
};
